/*
 * MSapling API Core Interface
 * (Refactored - April 2026 - Strangler Pattern)
 * Core logic migrated to ./api/client.ts
 */

import apiClient, { API_URL, getClientSessionId, getCsrfToken } from "./api/client";
import { logger } from "./utils/logger";
import { logTelemetryEvent, getSessionTraceId } from "./telemetry";

export { API_URL, getClientSessionId };
export const lastTraceId: string = getSessionTraceId();

// ── CSRF dedup + invalidation ────────────────────────────────────────────────
//
// The backend sets a non-httponly cookie `msapling_csrftoken` on every GET
// (priming). Concurrent requests that race before any GET has landed would
// each independently fire a priming GET, causing a token-rotation storm.
// We deduplicate that priming fetch: all callers share the same in-flight
// Promise<string> and the resolved token is cached until explicitly
// invalidated (e.g. after a privilege escalation that rotates the cookie).
//
// getCsrfToken() in client.ts ALWAYS reads the cookie fresh — this cache
// sits one layer above and is used only when a caller needs to force-prime
// the token before a mutating request rather than relying on getCsrfToken
// returning a fallback from session-storage.

let _csrfFetchPromise: Promise<string> | null = null;
let _csrfCachedToken: string | null = null;

/**
 * Invalidates the cached/primed CSRF token so the next call to
 * `primeCsrfToken()` re-fires the priming GET and reads the cookie fresh.
 * Call after any privilege-escalation flow that rotates the server cookie.
 */
export const invalidateCsrfToken = (): void => {
  _csrfCachedToken = null;
  _csrfFetchPromise = null;
};

/**
 * Primes the CSRF cookie via a single shared GET, deduplicating concurrent
 * callers. If the cookie is already present (getCsrfToken() returns a value)
 * or a priming fetch is already in-flight, no extra request is issued.
 * Returns the live CSRF token string (empty string if priming fails).
 */
export const primeCsrfToken = (): Promise<string> => {
  // Fast path: cookie is already readable (the common case after first load).
  const live = getCsrfToken();
  if (live) {
    _csrfCachedToken = live;
    _csrfFetchPromise = null;
    return Promise.resolve(live);
  }

  // Return the in-flight promise if one exists (dedup).
  if (_csrfFetchPromise) return _csrfFetchPromise;

  // No cookie, no in-flight fetch — fire the priming GET now and share it.
  _csrfFetchPromise = (async (): Promise<string> => {
    try {
      // A GET to /health is safe, cheap, and always reaches the middleware
      // that stamps the CSRF cookie. We use a minimal direct fetch() here
      // (rather than buildStreamRequestInit which is defined later in the
      // module) so there is no const-hoisting dependency.
      await fetch(`${API_URL}/health`, {
        method: "GET",
        credentials: "include",
        headers: {
          "X-Session-ID": getClientSessionId(),
          "X-Client-Type": "web",
        },
      });
      const token = getCsrfToken(); // reads from live cookie, populated by the GET above
      _csrfCachedToken = token;
      return token;
    } catch {
      return "";
    } finally {
      // Clear the pending reference so the next call after a failure retries.
      _csrfFetchPromise = null;
    }
  })();

  return _csrfFetchPromise;
};

/** Standardizes URL construction (Legacy support) */
export const buildUrl = (endpoint: string): string => {
  if (typeof endpoint === "string" && /^https?:\/\//i.test(endpoint)) return endpoint;
  const cleanPath = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${API_URL}${cleanPath}`;
};

/**
 * 💎 Enterprise Fetch Wrapper (Strangler Implementation)
 * Powered by axios-based apiClient but returns a fetch-compatible Response.
 * Implements exponential backoff retry for transient failures.
 */
const REQUEST_RETRY_ATTEMPTS = 3;
const REQUEST_RETRY_BASE_DELAY_MS = 400;

// Exported for guardrail tests
export const SAFE_RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const computeRetryDelayMs = (attempt: number, baseDelayMs: number): number => {
    // Exponential backoff with jitter
    const exponential = Math.pow(2, attempt - 1) * baseDelayMs;
    const jitter = exponential * 0.2 * Math.random();
    return exponential + jitter;
}

const isRetryableStatus = (status: number): boolean => {
    return SAFE_RETRYABLE_STATUS_CODES.includes(status);
}

export function isRetryableError(error: any): boolean {
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') return false;
  if (!error.response && error.request) return true; // Network error
  const status = error.response?.status;
  return status ? isRetryableStatus(status) : false;
}

// Mirror the header/CSRF injection that apiClient's request interceptor
// applies. Used by the native-fetch streaming path which can't go through
// axios (axios buffers the whole body, killing response.body streaming).
const buildStreamRequestInit = (
  method: string,
  endpoint: string,
  body: any,
  extraHeaders: Record<string, string> | undefined,
  signal: AbortSignal | undefined,
): { url: string; init: RequestInit } => {
  const cleanPath = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const isIdentity =
    cleanPath.startsWith("/auth") ||
    cleanPath.startsWith("/users") ||
    ["/health", "/ready"].includes(cleanPath);
  const path = !isIdentity && !cleanPath.startsWith("/api") ? `/api${cleanPath}` : cleanPath;
  const url = `${API_URL}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/x-ndjson, application/json",
    "X-Session-ID": getClientSessionId(),
    "X-Client-Type": "web",
    "X-Request-ID": (typeof crypto !== "undefined" && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    "X-Trace-ID": getSessionTraceId(),
    ...(extraHeaders || {}),
  };

  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  return {
    url,
    init: {
      method,
      headers,
      credentials: "include", // send/receive cookies (auth + csrf)
      signal,
      body: body == null
        ? undefined
        : (typeof body === "string" ? body : JSON.stringify(body)),
    },
  };
};

export const fetchWithAuth = async (endpoint: string, options: any = {}): Promise<Response> => {
  const method = (options.method || "GET").toUpperCase();
  const maxAttempts = options.maxAttempts || REQUEST_RETRY_ATTEMPTS;
  const baseDelayMs = options.baseDelayMs || REQUEST_RETRY_BASE_DELAY_MS;
  // One logical mutation must keep one idempotency key across all transport
  // retries.  The backend has legacy consumers using both header spellings,
  // so emit both with the same value.  Callers may provide a stable key when
  // retrying the whole operation from UI state.
  const isMutation = !["GET", "HEAD", "OPTIONS"].includes(method);
  const idempotencyKey = isMutation
    ? String(options.idempotencyKey || (
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `msapling-${Date.now()}-${Math.random().toString(36).slice(2)}`
    ))
    : "";
  const requestHeaders: Record<string, string> = {
    ...(options.headers || {}),
    ...(idempotencyKey ? {
      "Idempotency-Key": idempotencyKey,
      "X-Idempotency-Key": idempotencyKey,
    } : {}),
  };

  // STREAMING PATH (opt-in via options.stream === true)
  //
  // CHAT-STREAM-FIX (2026-06-02): the default axios path uses
  // responseType: 'arraybuffer' which buffers the entire body before
  // returning. The Response wrapper at the end has `.body === null`
  // (Response from ArrayBuffer doesn't expose a stream), so the NDJSON
  // reader at handleStreamingResponse exits immediately with zero
  // chunks and the assistant bubble stays empty.
  //
  // For streaming endpoints (chat/message), use native fetch() which
  // exposes a real ReadableStream<Uint8Array>. We mirror the
  // header/CSRF/credentials injection that the axios interceptors do.
  // We do NOT retry streaming requests automatically — partial side
  // effects (slot acquisition, billing preflight, partial assistant
  // bubble) make blind retries unsafe.
  if (options.stream === true) {
    const { url, init } = buildStreamRequestInit(
      method, endpoint, options.body, requestHeaders, options.signal,
    );
    try {
      // Streaming is a mutating POST: the backend does not echo X-CSRF-Token on
      // mutating responses (it only sets the cookie). The live cookie is the
      // source of truth and is read fresh on the next request, so there is
      // nothing to persist here — getCsrfToken() always reads document.cookie.
      return await fetch(url, init);
    } catch (e: any) {
      logger.error(`[API_STREAM_ERR] ${endpoint}`, e);
      throw e;
    }
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const response = await apiClient.request({
        url: endpoint,
        method: method,
        data: options.body,
        headers: requestHeaders,
        signal: options.signal,
        timeout: Number.isFinite(options.timeoutMs)
          ? Math.max(1000, Math.min(300000, Number(options.timeoutMs)))
          : undefined,
        responseType: 'arraybuffer',
        validateStatus: () => true,
      });

      if (isRetryableStatus(response.status) && attempt < maxAttempts) {
        throw {
          message: `Retryable status: ${response.status}`,
          response: response,
          isRetry: true
        };
      }

      const headers = new Headers();
      Object.entries(response.headers).forEach(([k, v]) => {
        if (v) headers.set(k, String(v));
      });

      return new Response(response.data, {
        status: response.status,
        statusText: response.statusText,
        headers: headers,
      });
    } catch (requestError: any) {
      // Cancellation is caller intent, not a network failure or failed search.
      if (options.signal?.aborted || requestError?.code === 'ERR_CANCELED' || requestError?.name === 'CanceledError' || requestError?.name === 'AbortError') {
        throw requestError;
      }
      if (!isRetryableError(requestError) || attempt >= maxAttempts) {
        // options.silent: opt-out of the error log for non-essential best-effort
        // requests (e.g. ghost-text autocomplete) whose failure is expected and
        // already handled benignly by the caller. The error is still thrown so
        // the caller can react; we just don't spam [API_FETCH_ERR] to the console.
        if (!options.silent) {
          logger.error(`[API_FETCH_ERR] ${endpoint} (final attempt ${attempt}/${maxAttempts})`, requestError);
        }
        throw requestError;
      }

      await sleep(computeRetryDelayMs(attempt, baseDelayMs));
    }
  }

  throw new Error(`Failed to fetch ${endpoint} after ${maxAttempts} attempts`);
};

/**
 * Fetches the list of available LLM models.
 */
export const fetchModels = async (signal?: AbortSignal): Promise<any[]> => {
  try {
    const res = await fetchWithAuth("/chat/models", { signal });
    const data = await res.json();
    return Array.isArray(data) ? data : (data.models || []);
  } catch (err) {
    if (signal?.aborted) throw err;
    logger.warn("[RECOVERY] Model fetch failed, using internal fallbacks.", err);
    return [
      { id: "openai/gpt-6-luna", name: "GPT-6 Luna" },
      { id: "openai/gpt-4o", name: "GPT-4o" },
    ];
  }
};

/**
 * Handles real-time NDJSON streaming responses from the backend.
 */
export const handleStreamingResponse = async (
  response: Response,
  onChunk: (data: any) => void,
  signal?: AbortSignal
): Promise<void> => {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let remainder = "";
  const chunkParts: string[] = [];
  const MAX_CHUNK_BYTES = 10 * 1024 * 1024; // 10MB
  let totalBytes = 0;

  const flushBufferedLines = (): void => {
    if (!chunkParts.length && !remainder) return;
    const merged = remainder + chunkParts.join("");
    chunkParts.length = 0;
    if (!merged) {
      remainder = "";
      return;
    }
    const lines = merged.split("\n");
    remainder =
lines.pop() || "";
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        onChunk(JSON.parse(trimmed));
      } catch {
        logTelemetryEvent({
          event: "stream_parse_error",
          category: "streaming",
          action: "ndjson_parse_failed",
          fragment: trimmed.slice(0, 80),
          url: response.url,
        });
        onChunk({
          error: `Stream format error: ${trimmed.slice(0, 80)}`,
        });
      }
    }
  };

  const onAbort = () => { reader.cancel(); };
  signal?.addEventListener("abort", onAbort, { once: true });

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      const decoded = decoder.decode(value, { stream: true });
      if (!decoded) continue;
      totalBytes += decoded.length;
      if (totalBytes > MAX_CHUNK_BYTES) throw new Error('Response stream exceeded 10MB limit');
      chunkParts.push(decoded);
      if (decoded.includes("\n")) flushBufferedLines();
      }
      const tail = decoder.decode();
      if (tail) {
      totalBytes += tail.length;
      if (totalBytes > MAX_CHUNK_BYTES) throw new Error('Response stream exceeded 10MB limit');
      chunkParts.push(tail);
      }
    flushBufferedLines();
  } finally {
    signal?.removeEventListener("abort", onAbort);
    reader.releaseLock();
  }
};

export interface MultiChatRequest {
  prompt: string;
  models: string[];
  project_name?: string;
  chat_id?: string;
  temperature?: number;
}

export interface MultiChatResult {
  model: string;
  response: string;
  tokens?: number;
  cost?: number;
  error?: string;
}

/** Multi-model chat via axios */
export const multiChat = async (req: MultiChatRequest): Promise<MultiChatResult[]> => {
  const resp = await fetchWithAuth('chat/multi', {
    method: 'POST',
    body: req,
  });
  if (!resp.ok) throw new Error(`Multi-chat failed: ${resp.status}`);
  const data = await resp.json();
  return Array.isArray(data) ? data : (data.results ?? []);
};

export interface ActiveStreamOwner {
  chat_id: string;
  status: string;
  started_at: number;
  client_type: string;
  session_id: string;
}

export interface ActiveStreamsInfo {
  active_stream_count: number;
  active_chat_ids: string[];
  active_streams?: ActiveStreamOwner[];
  limit?: number;
  account_active_stream_count?: number | null;
  account_limit?: number;
  tier?: string;
}

/** Active streams via axios */
export const getActiveStreams = async (): Promise<ActiveStreamsInfo> => {
  const resp = await fetchWithAuth('/chat/active-streams');
  if (!resp.ok) throw new Error(`Failed to get stream info: ${resp.status}`);
  return resp.json();
};

// --- CHAT-CHECKOUT-01: opt-in per-chat checkout lock --------------------------
export interface ChatCheckoutStatus {
  checked_out: boolean;
  chat_id: string;
  enabled?: boolean;
  mine?: boolean;
  owner_session?: string | null;
  expires_at?: number;
}

/** Current checkout status for a chat (held? mine? expiry?). */
export const getChatCheckout = async (chatId: string): Promise<ChatCheckoutStatus> => {
  const resp = await fetchWithAuth(`/chat/${encodeURIComponent(chatId)}/checkout`);
  if (!resp.ok) throw new Error(`checkout status ${resp.status}`);
  return resp.json();
};

/** Check out (exclusively lock) a chat to this frontend session. */
export const checkoutChat = async (chatId: string): Promise<Response> =>
  fetchWithAuth(`/chat/${encodeURIComponent(chatId)}/checkout`, { method: 'POST' });

/** Release a chat checkout (owner). */
export const releaseChatCheckout = async (chatId: string): Promise<Response> =>
  fetchWithAuth(`/chat/${encodeURIComponent(chatId)}/checkout`, { method: 'DELETE' });

/** Extend a held checkout lease. */
export const heartbeatChatCheckout = async (chatId: string): Promise<Response> =>
  fetchWithAuth(`/chat/${encodeURIComponent(chatId)}/checkout/heartbeat`, { method: 'POST' });
